import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Lock, ArrowLeft, Users, LogOut, Activity, CheckCircle2, ExternalLink } from "lucide-react";
import { firePixel } from "../lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIXEL_ID = "869435795902327";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [testFired, setTestFired] = useState(false);
  const [tgStatus, setTgStatus] = useState(null);
  const pixelLoaded = typeof window !== "undefined" && !!window.fbq;

  const testTelegram = async () => {
    setTgStatus("loading");
    try {
      const res = await axios.post(`${API}/admin/telegram/test`, {}, { headers: { "X-Admin-Password": password } });
      if (!res.data.configured) setTgStatus("not_configured");
      else setTgStatus(res.data.sent ? "sent" : "failed");
    } catch {
      setTgStatus("failed");
    }
  };

  const sendTestEvent = () => {
    firePixel("Lead", { content_name: "admin_test_event", test_event: true });
    if (window.fbq) window.fbq("trackCustom", "Cric1xAdminTest", { source: "admin_panel" });
    setTestFired(true);
    setTimeout(() => setTestFired(false), 4000);
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      const res = await axios.get(`${API}/leads`, {
        headers: { "X-Admin-Password": password },
      });
      setLeads(res.data);
      setAuthed(true);
    } catch (err) {
      setError("Invalid password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    window.open(`${API}/leads/export?password=${encodeURIComponent(password)}`, "_blank");
  };

  const logout = () => {
    setAuthed(false);
    setPassword("");
    setLeads([]);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-5 font-body text-white">
        <div className="noise-overlay" />
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm border border-gold/20 bg-surface p-8"
          data-testid="admin-login-form"
        >
          <Link to="/" className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/40 hover:text-gold">
            <ArrowLeft size={13} /> Back
          </Link>
          <div className="mb-6 flex items-center gap-3">
            <Lock className="text-gold" size={22} />
            <h1 className="font-display text-2xl font-bold uppercase">Leads Dashboard</h1>
          </div>
          <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
            Admin Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mt-2 w-full border-b border-white/15 bg-transparent py-3 text-lg text-white outline-none focus:border-gold"
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-gold py-3.5 font-display text-sm font-bold uppercase tracking-widest text-black transition-transform hover:scale-[0.99] disabled:opacity-60"
            data-testid="admin-signin-btn"
          >
            {loading ? "..." : "Sign In"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian px-5 py-10 font-body text-white">
      <div className="noise-overlay" />
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-3">
            <Users className="text-gold" size={26} />
            <h1 className="font-display text-3xl font-bold uppercase">Leads Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 bg-gold px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-black transition-transform hover:scale-[0.98]"
              data-testid="admin-export-btn"
            >
              <Download size={16} /> Export CSV
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-white/70 hover:border-gold hover:text-gold"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 font-mono text-[12px] uppercase tracking-widest text-white/50">
          <span className="text-gold" data-testid="admin-total-count">{leads.length}</span> Total Leads
        </div>

        {/* META PIXEL STATUS */}
        <div className="mt-8 border border-white/10 bg-surface p-6" data-testid="pixel-panel">
          <div className="flex items-center gap-3">
            <Activity className="text-gold" size={22} />
            <h2 className="font-display text-xl font-bold uppercase">Meta Pixel Status</h2>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <div className="border border-white/10 bg-black p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Pixel ID</p>
              <p className="mt-2 font-mono text-sm text-gold" data-testid="pixel-id">{PIXEL_ID}</p>
            </div>
            <div className="border border-white/10 bg-black p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Script Loaded</p>
              <p
                className={`mt-2 flex items-center gap-1.5 font-mono text-sm ${pixelLoaded ? "text-wa" : "text-red-400"}`}
                data-testid="pixel-status"
              >
                {pixelLoaded ? <><CheckCircle2 size={14} /> Active</> : "Not detected"}
              </p>
            </div>
            <div className="border border-white/10 bg-black p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Fires On</p>
              <p className="mt-2 font-mono text-sm text-white/70">Registration + Spin</p>
            </div>
          </div>

          <button
            onClick={sendTestEvent}
            className="mt-5 flex items-center gap-2 border border-gold/50 bg-gold/5 px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-gold transition-colors hover:bg-gold/10"
            data-testid="pixel-test-btn"
          >
            <Activity size={16} /> {testFired ? "Test Event Sent ✓" : "Send Test Event"}
          </button>

          <div className="mt-6 space-y-2 text-sm leading-relaxed text-white/60">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Pixel kaise verify kare</p>
            <p>1. Chrome me <b className="text-white">"Meta Pixel Helper"</b> extension install karo — landing page kholo, wo green dikhayega aur PageView + Lead events count karega.</p>
            <p>2. <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold underline">Meta Events Manager <ExternalLink size={12} /></a> → apna Pixel ({PIXEL_ID}) → <b className="text-white">Test Events</b> tab → landing page URL daalo → form submit / spin karo → live events yaha dikhenge.</p>
            <p>3. Upar <b className="text-white">"Send Test Event"</b> dabao — ye ek <span className="font-mono text-gold">Lead</span> + <span className="font-mono text-gold">Cric1xAdminTest</span> event turant fire karta hai (browser console me bhi log hota hai).</p>
          </div>
        </div>

        {/* TELEGRAM NOTIFICATIONS */}
        <div className="mt-6 border border-white/10 bg-surface p-6" data-testid="telegram-panel">
          <div className="flex items-center gap-3">
            <Activity className="text-gold" size={22} />
            <h2 className="font-display text-xl font-bold uppercase">Telegram Lead Alerts</h2>
          </div>
          <p className="mt-3 text-sm text-white/60">
            Naya lead aate hi aapko Telegram pe instant message milega. Setup: Telegram me <b className="text-white">@BotFather</b> se bot banao → token milega. Fir apne bot ko message karo aur <b className="text-white">@userinfobot</b> se apna Chat ID lo. Dono values backend .env me daalni hoti hain.
          </p>
          <button
            onClick={testTelegram}
            disabled={tgStatus === "loading"}
            className="mt-5 flex items-center gap-2 border border-gold/50 bg-gold/5 px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-gold transition-colors hover:bg-gold/10 disabled:opacity-60"
            data-testid="telegram-test-btn"
          >
            {tgStatus === "loading" ? "Sending..." : "Send Telegram Test"}
          </button>
          {tgStatus === "sent" && <p className="mt-3 text-sm text-wa" data-testid="telegram-status">✓ Test message sent — Telegram check karo!</p>}
          {tgStatus === "not_configured" && <p className="mt-3 text-sm text-yellow-400" data-testid="telegram-status">Bot token / Chat ID abhi set nahi hai. Emergent chat me apna token bhejo, main set kar dunga.</p>}
          {tgStatus === "failed" && <p className="mt-3 text-sm text-red-400" data-testid="telegram-status">Message fail hua — token/chat ID check karo.</p>}
        </div>

        <div className="mt-6 overflow-x-auto border border-white/10">
          <table className="w-full min-w-[640px] text-left" data-testid="leads-table">
            <thead>
              <tr className="border-b border-white/10 bg-surface font-mono text-[11px] uppercase tracking-widest text-white/50">
                <th className="p-4">#</th>
                <th className="p-4">Name</th>
                <th className="p-4">Phone</th>
                <th className="p-4">Reward</th>
                <th className="p-4">Source</th>
                <th className="p-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-white/40">No leads yet.</td>
                </tr>
              )}
              {leads.map((l, i) => (
                <tr key={l.id} className="border-b border-white/5 text-sm hover:bg-white/[0.03]" data-testid="lead-row">
                  <td className="p-4 text-white/40">{i + 1}</td>
                  <td className="p-4 font-medium">{l.name}</td>
                  <td className="p-4 text-gold">{l.phone}</td>
                  <td className="p-4 text-white/70">{l.reward || "—"}</td>
                  <td className="p-4 text-white/50">{l.source || "—"}</td>
                  <td className="p-4 font-mono text-xs text-white/40">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
